import React, { useState, useEffect, useRef } from "react";

const API_BASE = `${(process.env.REACT_APP_BACKEND_API_URL_LOCAL || '').replace(/\/$/, "")}/dissemination`;

const MODULES = [
  { key: "cyclone", label: "Cyclone", icon: "🌀" },
  { key: "landslide", label: "Landslide", icon: "⛰️" },
  { key: "earthquake", label: "Earthquake", icon: "🌍" },
  { key: "flood", label: "Flood", icon: "🌊" },
  { key: "fire", label: "Fire", icon: "🔥" },
  { key: "lightning", label: "Lightning", icon: "⚡" },
  { key: "road", label: "Road Accident", icon: "🚗" },
  { key: "weather-alerts", label: "Weather Alerts", icon: "🌤️" },
];

/* ── helpers ─────────────────────────────────────────────── */
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

/* ── EmailTagInput ────────────────────────────────────────── */
function EmailTagInput({ emails, onChange }) {
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const add = (raw) => {
    const val = raw.trim();
    if (!val) return;
    if (!isValidEmail(val)) { setError(`"${val}" is not a valid email`); return; }
    if (emails.includes(val)) { setError(`"${val}" already added`); return; }
    onChange([...emails, val]);
    setInput("");
    setError("");
  };

  const remove = (email) => onChange(emails.filter((e) => e !== email));

  const handleKey = (e) => {
    if (["Enter", ",", " ", "Tab"].includes(e.key)) {
      e.preventDefault();
      add(input);
    } else if (e.key === "Backspace" && !input && emails.length) {
      remove(emails[emails.length - 1]);
    } else {
      setError("");
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    const parts = pasted.split(/[\s,;]+/).filter(Boolean);
    const valid = [];
    const invalid = [];
    parts.forEach((p) => {
      if (isValidEmail(p) && !emails.includes(p)) valid.push(p);
      else if (!isValidEmail(p)) invalid.push(p);
    });
    if (valid.length) onChange([...emails, ...valid]);
    if (invalid.length) setError(`Skipped invalid: ${invalid.join(", ")}`);
  };

  return (
    <div>
      <div
        className="email-tag-box"
        onClick={() => inputRef.current?.focus()}
      >
        {emails.map((em) => (
          <span key={em} className="email-tag">
            {em}
            <button
              type="button"
              className="tag-remove"
              onClick={(e) => { e.stopPropagation(); remove(em); }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          className="tag-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          onPaste={handlePaste}
          onBlur={() => input && add(input)}
          placeholder={emails.length === 0 ? "Type email and press Enter or comma…" : ""}
        />
      </div>
      {error && <p className="field-error">{error}</p>}
      <p className="field-hint">Press Enter, comma, space or Tab to add · Paste multiple emails at once</p>
    </div>
  );
}

/* ── ModuleSelector ───────────────────────────────────────── */
function ModuleSelector({ selected, onChange }) {
  const toggle = (key) => {
    onChange(
      selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]
    );
  };
  return (
    <div className="module-grid">
      {MODULES.map((m) => (
        <button
          key={m.key}
          type="button"
          className={`module-chip ${selected.includes(m.key) ? "active" : ""}`}
          onClick={() => toggle(m.key)}
        >
          <span className="chip-icon">{m.icon}</span>
          <span className="chip-label">{m.label}</span>
          {selected.includes(m.key) && <span className="chip-check">✓</span>}
        </button>
      ))}
    </div>
  );
}

/* ── GroupFormModal ───────────────────────────────────────── */
function GroupFormModal({ group, onClose, onSaved }) {
  const isEdit = Boolean(group?.id);
  const [name, setName] = useState(group?.name || "");
  const [description, setDescription] = useState(group?.description || "");
  const [emails, setEmails] = useState(group?.emails?.map((e) => e.email) || []);
  const [modules, setModules] = useState(group?.modules?.map((m) => m.module) || []);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = "Group name is required";
    if (!modules.length) e.modules = "Select at least one module";
    if (!emails.length) e.emails = "Add at least one email address";
    return e;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      const url = isEdit ? `${API_BASE}/groups/${group.id}/` : `${API_BASE}/groups/`;
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), description, emails, modules }),
      });
      const data = await res.json();

      // if (!res.ok) throw new Error(JSON.stringify(data));
      if (!res.ok) {
        console.error("Save group failed:", data);
        throw new Error(JSON.stringify(data));
      }

      onSaved(data);
    }

    // catch (err) {
    //   setErrors({ global: "Failed to save group. Please try again." });
    // } 

    catch (err) {
      try {
        const parsed = JSON.parse(err.message);
        const msg = Object.entries(parsed)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
          .join(" | ");
        setErrors({ global: msg || "Failed to save group. Please try again." });
      } catch {
        setErrors({ global: err.message || "Failed to save group. Please try again." });
      }
    }

    finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? "Edit Group" : "Create New Group"}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {errors.global && <div className="alert-error">{errors.global}</div>}

          {/* Group Name */}
          <div className="field">
            <label className="field-label">Group Name <span className="req">*</span></label>
            <input
              className={`field-input ${errors.name ? "has-error" : ""}`}
              value={name}
              onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: "" })); }}
              placeholder="e.g. Coastal Warning Team"
            />
            {errors.name && <p className="field-error">{errors.name}</p>}
          </div>

          {/* Description */}
          <div className="field">
            <label className="field-label">Description</label>
            <textarea
              className="field-input"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description…"
            />
          </div>

          {/* Modules */}
          <div className="field">
            <label className="field-label">
              Assign Disaster Modules <span className="req">*</span>
            </label>
            <ModuleSelector selected={modules} onChange={(v) => { setModules(v); setErrors((p) => ({ ...p, modules: "" })); }} />
            {errors.modules && <p className="field-error">{errors.modules}</p>}
          </div>

          {/* Emails */}
          <div className="field">
            <label className="field-label">
              Email Recipients <span className="req">*</span>
              <span className="count-badge">{emails.length}</span>
            </label>
            <EmailTagInput emails={emails} onChange={(v) => { setEmails(v); setErrors((p) => ({ ...p, emails: "" })); }} />
            {errors.emails && <p className="field-error">{errors.emails}</p>}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Group"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── DeleteConfirmModal ───────────────────────────────────── */
function DeleteConfirmModal({ group, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await fetch(`${API_BASE}/groups/${group.id}/`, { method: "DELETE" });
      onDeleted(group.id);
    } catch {
      setDeleting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Delete Group</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p>Are you sure you want to delete <strong>"{group.name}"</strong>? This will remove all associated emails and module assignments.</p>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={deleting}>Cancel</button>
          <button className="btn-danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Delete Group"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── GroupCard ────────────────────────────────────────────── */
function GroupCard({ group, onEdit, onDelete }) {
  const moduleMap = Object.fromEntries(MODULES.map((m) => [m.key, m]));
  return (
    <div className="group-card">
      <div className="card-top">
        <div>
          <h3 className="card-name">{group.name}</h3>
          {group.description && <p className="card-desc">{group.description}</p>}
        </div>
        <div className="card-actions">
          <button className="icon-btn edit" onClick={() => onEdit(group)} title="Edit">✏️</button>
          <button className="icon-btn delete" onClick={() => onDelete(group)} title="Delete">🗑️</button>
        </div>
      </div>

      <div className="card-modules">
        {group.modules.map((m) => {
          const meta = moduleMap[m.module];
          return (
            <span key={m.id} className="module-badge">
              {meta?.icon} {meta?.label || m.module}
            </span>
          );
        })}
      </div>

      <div className="card-emails">
        <span className="email-count-label">
          📧 {group.emails.length} recipient{group.emails.length !== 1 ? "s" : ""}
        </span>
        <div className="email-list">
          {group.emails.slice(0, 5).map((e) => (
            <span key={e.id} className="email-pill">{e.email}</span>
          ))}
          {group.emails.length > 5 && (
            <span className="email-pill more">+{group.emails.length - 5} more</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Main Page ────────────────────────────────────────────── */
export default function EmailDisseminationGroups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [deleteGroup, setDeleteGroup] = useState(null);
  const [search, setSearch] = useState("");

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/groups/`);
      const data = await res.json();
      setGroups(data);
    } catch {
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGroups(); }, []);

  const handleSaved = (saved) => {
    setGroups((prev) => {
      const exists = prev.find((g) => g.id === saved.id);
      return exists ? prev.map((g) => (g.id === saved.id ? saved : g)) : [saved, ...prev];
    });
    setShowCreate(false);
    setEditGroup(null);
  };

  const handleDeleted = (id) => {
    setGroups((prev) => prev.filter((g) => g.id !== id));
    setDeleteGroup(null);
  };

  const filtered = groups.filter((g) =>
    g.name.toLowerCase().includes(search.toLowerCase()) ||
    g.description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <style>{CSS}</style>
      <div className="page">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">Email Dissemination Groups</h1>
            <p className="page-sub">Manage notification groups for disaster alert modules</p>
          </div>
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Create Group
          </button>
        </div>

        {/* Search + stats */}
        <div className="toolbar">
          <input
            className="search-input"
            placeholder="Search groups…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="stat-chip">{groups.length} group{groups.length !== 1 ? "s" : ""}</span>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="loading-state">Loading groups…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <p className="empty-icon">📭</p>
            <p>{search ? "No groups match your search." : "No groups yet. Create your first group!"}</p>
          </div>
        ) : (
          <div className="group-grid">
            {filtered.map((g) => (
              <GroupCard
                key={g.id}
                group={g}
                onEdit={setEditGroup}
                onDelete={setDeleteGroup}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreate && (
        <GroupFormModal onClose={() => setShowCreate(false)} onSaved={handleSaved} />
      )}
      {editGroup && (
        <GroupFormModal group={editGroup} onClose={() => setEditGroup(null)} onSaved={handleSaved} />
      )}
      {deleteGroup && (
        <DeleteConfirmModal group={deleteGroup} onClose={() => setDeleteGroup(null)} onDeleted={handleDeleted} />
      )}
    </>
  );
}

/* ── CSS ──────────────────────────────────────────────────── */
const CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  .page {
    font-family: 'Segoe UI', system-ui, sans-serif;
    background: #ffffff;
    min-height: 100vh;
    padding: 28px 32px;
    color: #1e293b;
  }

  /* Header */
  .page-header {
    display: flex; align-items: flex-start; justify-content: space-between;
    margin-bottom: 24px; gap: 16px; flex-wrap: wrap;
  }
  .page-title { font-size: 1.7rem; font-weight: 700; color: #0f172a; }
  .page-sub   { color: #64748b; font-size: 0.9rem; margin-top: 4px; }

  /* Toolbar */
  .toolbar {
    display: flex; align-items: center; gap: 12px; margin-bottom: 20px;
  }
  .search-input {
    flex: 1; max-width: 360px; padding: 9px 14px; border: 1.5px solid #e2e8f0;
    border-radius: 8px; font-size: 0.9rem; outline: none; background: #fff;
    transition: border-color 0.2s;
  }
  .search-input:focus { border-color: #3b82f6; }
  .stat-chip {
    background: #dbeafe; color: #1e40af; font-size: 0.8rem;
    padding: 4px 12px; border-radius: 20px; font-weight: 600;
  }

  /* Grid */
  .group-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
    gap: 18px;
  }

  /* Card */
  .group-card {
    background: #fff; border-radius: 12px; padding: 20px;
    border: 1.5px solid #e2e8f0;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    transition: box-shadow 0.2s, border-color 0.2s;
  }
  .group-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.1); border-color: #bfdbfe; }

  .card-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; margin-bottom: 14px; }
  .card-name { font-size: 1rem; font-weight: 700; color: #0f172a; }
  .card-desc { font-size: 0.82rem; color: #64748b; margin-top: 3px; }
  .card-actions { display: flex; gap: 6px; flex-shrink: 0; }

  .icon-btn {
    background: none; border: none; cursor: pointer; font-size: 1rem;
    padding: 4px 6px; border-radius: 6px; transition: background 0.15s;
  }
  .icon-btn.edit:hover   { background: #eff6ff; }
  .icon-btn.delete:hover { background: #fff1f2; }

  .card-modules { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px; }
  .module-badge {
    background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0;
    font-size: 0.75rem; padding: 3px 10px; border-radius: 20px; font-weight: 500;
  }

  .card-emails { }
  .email-count-label { font-size: 0.8rem; color: #64748b; display: block; margin-bottom: 6px; }
  .email-list { display: flex; flex-wrap: wrap; gap: 5px; }
  .email-pill {
    background: #f8fafc; color: #475569; border: 1px solid #e2e8f0;
    font-size: 0.75rem; padding: 2px 8px; border-radius: 4px;
  }
  .email-pill.more { background: #eff6ff; color: #3b82f6; border-color: #bfdbfe; }

  /* Empty / Loading */
  .empty-state  { text-align: center; padding: 60px 20px; color: #94a3b8; }
  .empty-icon   { font-size: 2.5rem; margin-bottom: 10px; }
  .loading-state { text-align: center; padding: 60px; color: #94a3b8; font-size: 0.9rem; }

  /* Buttons */
  .btn-primary {
    background: #2563eb; color: #fff; border: none; cursor: pointer;
    padding: 10px 20px; border-radius: 8px; font-size: 0.9rem; font-weight: 600;
    transition: background 0.2s;
  }
  .btn-primary:hover:not(:disabled) { background: #1d4ed8; }
  .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

  .btn-secondary {
    background: #f1f5f9; color: #475569; border: 1.5px solid #e2e8f0; cursor: pointer;
    padding: 10px 20px; border-radius: 8px; font-size: 0.9rem; font-weight: 600;
    transition: background 0.2s;
  }
  .btn-secondary:hover:not(:disabled) { background: #e2e8f0; }
  .btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }

  .btn-danger {
    background: #ef4444; color: #fff; border: none; cursor: pointer;
    padding: 10px 20px; border-radius: 8px; font-size: 0.9rem; font-weight: 600;
    transition: background 0.2s;
  }
  .btn-danger:hover:not(:disabled) { background: #dc2626; }
  .btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }

  /* Modal */
  .modal-overlay {
    position: fixed; inset: 0; background: rgba(15,23,42,0.5);
    display: flex; align-items: center; justify-content: center;
    z-index: 1000; padding: 20px;
  }
  .modal {
    background: #fff; border-radius: 16px; width: 100%; max-width: 650px;
    max-height: 75vh; display: flex; flex-direction: column;
    box-shadow: 0 20px 60px rgba(0,0,0,0.25);
    margin-left: 30%; margin-top : 8%;
    
  }
  .modal-sm { max-width: 440px; }

  .modal-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 20px 24px 16px; border-bottom: 1.5px solid #f1f5f9;
  }
  .modal-header h2 { font-size: 1.1rem; font-weight: 700; color: #0f172a; }
  .modal-close {
    background: none; border: none; cursor: pointer; font-size: 1.4rem;
    color: #94a3b8; line-height: 1; transition: color 0.15s;
  }
  .modal-close:hover { color: #475569; }

  .modal-body { padding: 20px 24px; overflow-y: auto; flex: 1; }
  .modal-footer {
    padding: 16px 24px; border-top: 1.5px solid #f1f5f9;
    display: flex; gap: 10px; justify-content: flex-end;
  }

  /* Fields */
  .field { margin-bottom: 18px; }
  .field-label {
    display: flex; align-items: center; gap: 6px;
    font-size: 0.85rem; font-weight: 600; color: #374151; margin-bottom: 6px;
  }
  .req { color: #ef4444; }
  .count-badge {
    background: #dbeafe; color: #1e40af; font-size: 0.72rem;
    padding: 2px 7px; border-radius: 10px; font-weight: 700;
  }
  .field-input {
    width: 100%; padding: 9px 12px; border: 1.5px solid #e2e8f0;
    border-radius: 8px; font-size: 0.9rem; outline: none;
    transition: border-color 0.2s; font-family: inherit; resize: vertical;
  }
  .field-input:focus { border-color: #3b82f6; }
  .field-input.has-error { border-color: #ef4444; }
  .field-error { color: #ef4444; font-size: 0.78rem; margin-top: 4px; }
  .field-hint  { color: #94a3b8;  font-size: 0.75rem; margin-top: 5px; }

  /* Email tag box */
  .email-tag-box {
    display: flex; flex-wrap: wrap; gap: 6px; align-items: center;
    min-height: 44px; padding: 6px 10px; border: 1.5px solid #e2e8f0;
    border-radius: 8px; cursor: text; background: #fff;
    transition: border-color 0.2s;
  }
  .email-tag-box:focus-within { border-color: #3b82f6; }

  .email-tag {
    display: flex; align-items: center; gap: 5px;
    background: #dbeafe; color: #1d4ed8; font-size: 0.8rem;
    padding: 3px 8px 3px 10px; border-radius: 20px; font-weight: 500;
  }
  .tag-remove {
    background: none; border: none; cursor: pointer; color: #3b82f6;
    font-size: 1rem; line-height: 1; padding: 0; transition: color 0.15s;
  }
  .tag-remove:hover { color: #1d4ed8; }

  .tag-input {
    flex: 1; min-width: 160px; border: none; outline: none;
    font-size: 0.85rem; font-family: inherit; padding: 2px 4px;
    background: transparent; color: #1e293b;
  }

  /* Module chips */
  .module-grid {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 8px;
  }
  .module-chip {
    display: flex; align-items: center; gap: 7px;
    padding: 9px 12px; border: 1.5px solid #e2e8f0;
    border-radius: 8px; background: #f8fafc; cursor: pointer;
    font-size: 0.83rem; color: #475569; font-weight: 500;
    transition: all 0.15s; position: relative;
    font-family: inherit;
  }
  .module-chip:hover { border-color: #93c5fd; background: #eff6ff; color: #2563eb; }
  .module-chip.active {
    border-color: #3b82f6; background: #eff6ff; color: #1d4ed8; font-weight: 600;
  }
  .chip-icon  { font-size: 1rem; }
  .chip-label { flex: 1; text-align: left; }
  .chip-check {
    font-size: 0.75rem; color: #2563eb; background: #dbeafe;
    border-radius: 50%; width: 18px; height: 18px;
    display: flex; align-items: center; justify-content: center;
  }

  /* Alert */
  .alert-error {
    background: #fff1f2; color: #be123c; border: 1px solid #fecdd3;
    padding: 10px 14px; border-radius: 8px; font-size: 0.85rem; margin-bottom: 16px;
  }
`;